/**
 * AGC: Shared utilities for syncing the QA debug toggles across tabs, sockets, and builds.
 */

export interface DebugSettings {
  debugButtonEnabled: boolean
  erudaButtonEnabled: boolean
  copyButtonEnabled: boolean
  showEmergencyWithdrawPopup: boolean // AGC: Legacy - kept for backward compatibility
  showEmergencyWithdrawStakingManager: boolean // AGC: Show StakingManager button in popup
  showEmergencyWithdrawStakingRevenueShare: boolean // AGC: Show StakingRevenueShare button in popup
}

export type DebugSettingKey = keyof DebugSettings

export interface DebugSettingsEventDetail {
  settings: DebugSettings
  lastUpdatedAt?: string | null
}

export const DEBUG_SETTINGS_STORAGE_KEY = 'ogb_debug_settings_v1'
export const DEBUG_SETTINGS_EVENT = 'ogb:debug-settings-updated'

export const DEFAULT_DEBUG_SETTINGS: Readonly<DebugSettings> = {
  debugButtonEnabled: true,
  erudaButtonEnabled: true,
  copyButtonEnabled: true,
  showEmergencyWithdrawPopup: false, // AGC: Legacy - kept for backward compatibility
  showEmergencyWithdrawStakingManager: false, // AGC: StakingManager button hidden by default
  showEmergencyWithdrawStakingRevenueShare: false, // AGC: StakingRevenueShare button hidden by default
}

export const normalizeDebugSettings = (incoming: Partial<DebugSettings> | undefined | null): DebugSettings => ({
  ...DEFAULT_DEBUG_SETTINGS,
  ...(incoming ?? {}),
})

export const readDebugSettingsFromStorage = (): DebugSettings => {
  if (typeof window === 'undefined') {
    return { ...DEFAULT_DEBUG_SETTINGS }
  }

  try {
    const raw = window.localStorage.getItem(DEBUG_SETTINGS_STORAGE_KEY)
    if (!raw) {
      return { ...DEFAULT_DEBUG_SETTINGS }
    }
    const parsed = JSON.parse(raw) as Partial<DebugSettings>
    return normalizeDebugSettings(parsed)
  } catch (error) {
    console.warn('AGC: Failed to parse debug settings from storage, resetting.', error)
    return { ...DEFAULT_DEBUG_SETTINGS }
  }
}

export const persistDebugSettingsToStorage = (settings: DebugSettings): void => {
  if (typeof window === 'undefined') {
    return
  }

  try {
    window.localStorage.setItem(DEBUG_SETTINGS_STORAGE_KEY, JSON.stringify(settings))
  } catch (error) {
    console.warn('AGC: Unable to persist debug settings to storage.', error)
  }
}

